import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import RequestQuestionStep from '@app/src/components/CreateNewRequestModal/RequestQuestionStep'
import CreationModalContainer from '@app/src/components/CreationModal/CreationModalContainer'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import { ProgressTracker } from '@app/src/types/resourceExplorer'
import { isNotPositiveWholeNumber } from '@app/src/utils/helpers'
import { br } from '@app/src/utils/translationMarkup'
import { NotificationSeverity } from '@app/src/wf-constants'
import { Dialog } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useQueryClient } from 'react-query'
import PeriodOffsetField from './PeriodOffsetField'
import ProviderScopeField from './ProviderScopeField'
import TotalCompaniesCountField from './TotalCompaniesCountField'

export enum PROGRESS_TRACKER_QUESTION_KEYS {
  QUESTIONNAIRE_TEMPLATE_ID = 'questionnaireTemplateId',
  TOTAL_COMPANIES_IN_SCOPE = 'totalCompaniesInScope',
  SCOPE_TARGET_PERIOD_NAME = 'scopeTargetPeriodName',
  TRACKING_PERIOD_OFFSET = 'trackingPeriodOffset',
  TOTAL_COMPANIES_COUNT = 'totalCompaniesCount',
}

type CreateProgressTrackerModalProps = {
  onClose: () => void
  open: boolean
  progressTrackerItem?: ProgressTracker
  questionnaireTemplateId: number
}

export interface ProgressTrackerFormType {
  questionnaireTemplateId: number
  totalCompaniesInScope: number
  scopeTargetPeriodName: string
  trackingPeriodOffset: number
  totalCompaniesCount: number
}

const CreateProgressTrackerModal: React.FC<CreateProgressTrackerModalProps> = ({
  onClose,
  open,
  progressTrackerItem,
  questionnaireTemplateId,
}) => {
  const { setActiveStep, activeStep, setTotalSteps } = useCreationModalProgress()
  const { formatMessage } = useIntl()
  const { mutateAsync: createRequest, isLoading } = useCreateResource()
  const { showErrorNotification } = useErrorNotification()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const { settings, updateOrganizationSettings, isSaving } = useOrganizationSettings()
  const totalCompaniesCount = settings?.totalCompaniesCount

  const formMethods = useForm<ProgressTrackerFormType>({
    shouldUnregister: false,
    defaultValues: useMemo(
      () => ({
        ...progressTrackerItem,
        totalCompaniesCount: totalCompaniesCount || 0,
      }),
      [progressTrackerItem, totalCompaniesCount],
    ),
  })

  const { getValues, reset } = formMethods

  const handleClose = () => {
    onClose()
  }

  const canGoNextForTotalCompanies = (values: unknown) => {
    const formValues = values as ProgressTrackerFormType
    const totalCompaniesCountValue = formValues[PROGRESS_TRACKER_QUESTION_KEYS.TOTAL_COMPANIES_COUNT]

    const hasValue =
      totalCompaniesCountValue !== null &&
      totalCompaniesCountValue !== undefined &&
      !Number.isNaN(totalCompaniesCountValue)

    const isValidValue = hasValue && !isNotPositiveWholeNumber(totalCompaniesCountValue)

    return isValidValue
  }

  useEffect(() => {
    if (open) {
      reset({
        ...progressTrackerItem,
        totalCompaniesCount: totalCompaniesCount || 0,
      })
    }
  }, [open, progressTrackerItem, totalCompaniesCount])
  const handleFormSubmit = async () => {
    const currentValues = getValues()

    const totalCompaniesInScope =
      currentValues.totalCompaniesInScope !== undefined
        ? Number(currentValues.totalCompaniesInScope)
        : progressTrackerItem?.totalCompaniesInScope

    const scopeTargetPeriodName = currentValues.scopeTargetPeriodName || progressTrackerItem?.scopeTargetPeriodName

    const trackingPeriodOffset =
      currentValues.trackingPeriodOffset !== undefined
        ? Number(currentValues.trackingPeriodOffset)
        : progressTrackerItem?.trackingPeriodOffset

    const body = {
      id: progressTrackerItem?.id,
      questionnaireTemplateId,
      totalCompaniesInScope,
      scopeTargetPeriodName,
      trackingPeriodOffset,
    }

    const successMessage = progressTrackerItem?.id ? 'editSuccessMessage' : 'successMessage'

    await createRequest(
      { url: endpoints.saveProgressTracker, body },
      {
        onSuccess: () => {
          showSnackbar({
            message: formatMessage({ id: `schemas.progressTracker.${successMessage}` }),
            severity: NotificationSeverity.success,
          })
          setActiveStep(1)
          reset({})
          queryClient.invalidateQueries([FetchKey.ProgressTracker])
          queryClient.invalidateQueries([FetchKey.ProviderCoverage])
          handleClose()
        },
        onError: error => {
          showErrorNotification({ requestError: error })
        },
      },
    )
  }

  const PROGRESS_TRACKER_QUESTIONS = useMemo(
    () => [
      {
        title: formatMessage({ id: 'schemas.progressTracker.createNewModal.supplyChainSizeTitle' }),
        description: formatMessage({ id: 'schemas.progressTracker.createNewModal.supplyChainSizeDescription' }),
        fieldnames: [PROGRESS_TRACKER_QUESTION_KEYS.TOTAL_COMPANIES_COUNT],
        isLoading: isSaving,
        canGoNext: canGoNextForTotalCompanies,
        customOnClickNext: async () => {
          const currentTotalCompaniesCount = getValues(PROGRESS_TRACKER_QUESTION_KEYS.TOTAL_COMPANIES_COUNT)
          if (!currentTotalCompaniesCount || Number(currentTotalCompaniesCount) === Number(totalCompaniesCount)) return
          await updateOrganizationSettings({ totalCompaniesCount: Number(currentTotalCompaniesCount) })
        },
        children: <TotalCompaniesCountField control={formMethods.control} totalCompaniesCount={totalCompaniesCount} />,
      },
      {
        title: formatMessage({ id: 'schemas.progressTracker.createNewModal.suppliersInScopeTitle' }),
        description: formatMessage(
          { id: 'schemas.progressTracker.createNewModal.suppliersInScopeDescription' },
          { br },
        ),
        fieldnames: [
          PROGRESS_TRACKER_QUESTION_KEYS.TOTAL_COMPANIES_IN_SCOPE,
          PROGRESS_TRACKER_QUESTION_KEYS.SCOPE_TARGET_PERIOD_NAME,
        ],
        children: <ProviderScopeField />,
      },
      {
        title: formatMessage({ id: 'schemas.progressTracker.createNewModal.periodOffsetTitle' }),
        description: formatMessage({ id: 'schemas.progressTracker.createNewModal.periodOffsetDescription' }),
        fieldnames: [PROGRESS_TRACKER_QUESTION_KEYS.TRACKING_PERIOD_OFFSET],
        children: <PeriodOffsetField />,
      },
    ],
    [formatMessage, getValues, isSaving, totalCompaniesCount, formMethods],
  )

  const activeQuestion = useMemo(() => PROGRESS_TRACKER_QUESTIONS[activeStep - 1], [activeStep])

  useEffect(() => {
    setTotalSteps(PROGRESS_TRACKER_QUESTIONS.length)
  }, [])

  return (
    <Dialog open={open} fullScreen onClose={handleClose}>
      <CreationModalContainer
        title={formatMessage({ id: 'schemas.progressTracker.createNewModal.title' })}
        onClose={() => {
          reset({})
          setActiveStep(1)
          handleClose()
        }}
      >
        <FormProvider {...formMethods}>
          {activeQuestion && (
            <RequestQuestionStep
              fieldnames={activeQuestion.fieldnames}
              title={activeQuestion.title}
              description={activeQuestion.description}
              key={activeQuestion.title}
              disableInitialBack
              onClose={handleClose}
              onBack={onClose}
              onSubmit={handleFormSubmit}
              isLoading={isLoading}
              editMode={Boolean(progressTrackerItem)}
              createGoalTrackerFlow
              customOnClickNext={activeQuestion.customOnClickNext}
              canGoNext={activeQuestion.canGoNext}
            >
              {activeQuestion.children}
            </RequestQuestionStep>
          )}
        </FormProvider>
      </CreationModalContainer>
    </Dialog>
  )
}

export default CreateProgressTrackerModal
